/** Relay URL helpers shared by the config layer and the protocol layer. */

const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i

/**
 * Normalises a user supplied relay address.
 *
 * - adds the `wss://` scheme when it is missing (`relay.damus.io` is accepted)
 * - only `ws:` and `wss:` URLs are valid relay addresses
 * - lowercases the host, drops the default port, fragments and query strings
 * - keeps sub-paths (`wss://lang.relays.land/zh`) and normalises the root to `/`
 */
export function normalizeRelayUrl(input: string | undefined | null): string | undefined {
  if (typeof input !== "string") return undefined
  let value = input.trim()
  if (!value) return undefined
  if (!SCHEME.test(value)) value = `wss://${value.replace(/^\/+/, "")}`

  let url: URL
  try {
    url = new URL(value)
  } catch {
    return undefined
  }

  if (url.protocol !== "wss:" && url.protocol !== "ws:") return undefined
  if (!url.hostname) return undefined
  if (url.username || url.password) return undefined
  if (!url.hostname.includes(".") && url.hostname !== "localhost" && !url.hostname.startsWith("[")) {
    return undefined
  }

  const protocol = url.protocol === "ws:" ? "ws:" : "wss:"
  const host = url.hostname.toLowerCase()
  const port = url.port && url.port !== "80" && url.port !== "443" ? `:${url.port}` : ""
  let path = url.pathname || "/"
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1)
  return `${protocol}//${host}${port}${path}`
}

export function isWebsocketUrl(value: string | undefined | null): boolean {
  return normalizeRelayUrl(value) !== undefined
}

/** `wss://nos.lol/` -> `nos.lol`, `wss://relay.example.com/zh` -> `relay.example.com/zh` */
export function simplifyUrl(value: string): string {
  const normalized = normalizeRelayUrl(value) ?? value
  return normalized.replace(/^wss?:\/\//, "").replace(/\/$/, "")
}

export function toHttpUrl(relayUrl: string): string {
  return relayUrl.replace(/^wss:/, "https:").replace(/^ws:/, "http:")
}

export function relayHost(relayUrl: string): string {
  try {
    return new URL(relayUrl).hostname
  } catch {
    return simplifyUrl(relayUrl)
  }
}

export function isOnionUrl(value: string): boolean {
  return relayHost(value).endsWith(".onion")
}

export function isLocalRelayUrl(value: string): boolean {
  const host = relayHost(value)
  if (host === "localhost" || host.endsWith(".local")) return true
  if (/^127\./.test(host) || host === "::1" || host === "[::1]") return true
  if (/^10\./.test(host) || /^192\.168\./.test(host)) return true
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(host)) return true
  return false
}

/** A relay discovery / review event must point at a public relay. */
export function isPublicRelayUrl(value: string, filterOnion = false): boolean {
  if (!isWebsocketUrl(value)) return false
  if (isLocalRelayUrl(value)) return false
  if (filterOnion && isOnionUrl(value)) return false
  return true
}

/** Builds a placeholder image URL from a relay host, used when NIP-11 has no icon. */
export function relayFaviconUrl(relayUrl: string): string | undefined {
  const host = relayHost(relayUrl)
  if (!host || isLocalRelayUrl(relayUrl)) return undefined
  return `https://${host}/favicon.ico`
}

export function uniqueRelayUrls(urls: Array<string | undefined | null>): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const url of urls) {
    const normalized = normalizeRelayUrl(url)
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    result.push(normalized)
  }
  return result
}

/**
 * Relays used for auxiliary lookups (profiles, referenced events, small lists).
 *
 * Relays enforce low limits on concurrent REQs (some as low as ~10), so lookups
 * deliberately use only the first few relays instead of every configured relay.
 * The primary feed/thread subscription still uses the full relay list.
 */
export function preferredLookupRelays(relays: string[], limit = 3): string[] {
  return relays.slice(0, Math.max(1, limit))
}
